/**
 * Store Mapper Application
 * Handles store search functionality with map integration
 */

class StoreMapper {
  constructor() {
    this.storemapperDiv = document.getElementById('storemapper');
    this.pageType = window.storemapperPageType || '';
    this.observers = new Map();
    this.processedElements = new WeakSet();
    this.initialized = false;
    
    this.init();
  }

  /**
   * Get URL parameter by name
   * @param {string} name - Parameter name
   * @returns {string|null} Parameter value
   */
  getUrlParam(name) {
    const url = new URL(window.location.href);
    return url.searchParams.get(name);
  }

  /**
   * Initialize the application
   */
  init() {
    if (!this.storemapperDiv) return;

    // First check if storemapper is already loaded
    if (this.isStoremapperReady()) {
      this.setupApplication();
      return;
    }

    // Set up observer for dynamic loading
    const observer = new MutationObserver(() => {
      if (this.isStoremapperReady()) {
        this.setupApplication();
        observer.disconnect();
      }
    });

    observer.observe(this.storemapperDiv, { 
      childList: true, 
      subtree: true 
    });

    // Fallback: Also try to initialize after a delay
    setTimeout(() => {
      if (this.isStoremapperReady() && !this.initialized) {
        this.setupApplication();
        observer.disconnect();
      }
    }, 1000);
  }

  /**
   * Check if storemapper is ready with actual content
   */
  isStoremapperReady() {
    if (!this.storemapperDiv || this.storemapperDiv.innerHTML.trim() === '') {
      return false;
    }

    // Check for key storemapper elements that indicate it's fully loaded
    const searchButton = document.getElementById('storemapper-go');
    const searchBox = document.getElementById('storemapper-zip');
    const mapContainer = document.querySelector('.strmpr-layout-horizontal .strmpr-map');
    
    // Storemapper is ready if core elements exist
    return !!(searchButton && searchBox && mapContainer);
  }

  /**
   * Setup the complete application
   */
  setupApplication() {
    if (this.initialized) return;
    
    this.addPageTypeClasses();
    this.setupElements();
    this.setupEventListeners();
    this.setupFilters();
    this.filterDropdownOptions();
    this.setupInitialState();
    this.processUrlParameters();
    this.arrangeDivs();
    this.addInputLabels();
    
    // Set initial map design
    this.handleMapDesign();
    
    if (this.pageType === 'designer-event') {
      this.handleCustomFunctionalities();
    }

    this.initialized = true;
  }

  /**
   * Add page type specific classes
   */
  addPageTypeClasses() {
    if (this.pageType === 'designer-event') {
      this.storemapperDiv.classList.add('strmpr-designer-event');
    } else if (this.pageType === 'find-retailer') {
      this.storemapperDiv.classList.add('strmpr-find-retailer');
    }
  }

  /**
   * Setup DOM elements references
   */
  setupElements() {
    this.elements = {
      searchButton: document.getElementById('storemapper-go'),
      searchZipButton: document.getElementById('storemapper-zip'),
      viewMoreButton: document.querySelector('.strmpr-view-more-stores-button'),
      resetButton: document.querySelector('.strmpr-search .strmpr-search-box-wrapper .strmpr-search-reset'),
      storemapperResults: document.querySelector('.strmpr-layout-horizontal .strmpr-search-results'),
      mapContainer: document.querySelector('.strmpr-layout-horizontal .strmpr-map'),
      pageHeader: document.querySelector('.main-page-title'),
      filterBucket: document.querySelector('.strmpr-filter-bucket')
    };
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    const { searchButton, searchZipButton, viewMoreButton, resetButton } = this.elements;

    // Checkbox change events
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('storemapper-filter-checkbox')) {
        this.handleCustomFunctionalities();
      }
    });

    // Search related events
    if (searchZipButton) {
      if (this.pageType === 'find-retailer') {
        this.toggleSearchButton();
        searchZipButton.addEventListener('input', () => this.toggleSearchButton());
      }
    }

    if (searchButton) {
    searchButton.addEventListener('click', () => {
      this.handleMapDesignOnSearch();
      
      setTimeout(() => {
        this.handleCustomFunctionalities();
      }, 300);
      
      this.setupSearchResultsObserver();
    });
  }

    if (viewMoreButton) {
      viewMoreButton.addEventListener('click', () => {
        setTimeout(() => {
          this.handleCustomFunctionalities();
        }, 200);
      });
    }

    if (resetButton) {
      resetButton.addEventListener('click', () => {
        this.handleMapDesignOnReset();
        
        setTimeout(() => {
          this.handleCustomFunctionalities();
        }, 100);
      });
    }
  }

  /**
   * Toggle search button state based on zip input
   */
  toggleSearchButton() {
    const { searchButton, searchZipButton } = this.elements;
    const hasValue = searchZipButton?.value?.trim();
    
    if (hasValue) {
      searchButton?.removeAttribute('disabled');
    } else {
      searchButton?.setAttribute('disabled', '');
    }
  }

  /**
   * Setup dropdown filters with classes
   */
  setupFilters() {
    const dropdownButtons = document.querySelectorAll('.strmpr-dropdown-btn');
    
    dropdownButtons.forEach(button => {
      const span = button.querySelector('span');
      if (!span?.textContent) return;

      const text = span.textContent.trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '-');

      const dropdownParent = button.closest('.strmpr-dropdown');
      dropdownParent?.classList.add(`strmpr-dropdown-${text}`);
    });

    this.setupTypeFilter();
  }

  /**
   * Filter dropdown options based on page type and storemapperCategories
   */
  filterDropdownOptions() {
    const dropdown = document.getElementById('storemapper-filter-drop-down-1');
    if (!dropdown) return;

    const options = dropdown.querySelectorAll('.strmpr-dropdown-option');
    if (!options.length) return;
    
    // Get categories from window global if available
    const categories = window.storemapperCategories || [];

    options.forEach(option => {
      const label = option.querySelector('label');
      if (!label) return;
      
      const labelText = label.textContent.trim();
      const labelTextLower = labelText.toLowerCase();
      
      // Skip filtering for "Select All" option
      if (labelTextLower === 'select all' || labelTextLower === 'all') {
        return;
      }
      
      // Hide by default, show only if matches a category
      if (categories.length > 0) {
        const matchesCategory = categories.some(category => 
          category.toLowerCase() === labelTextLower || 
          labelTextLower.includes(category.toLowerCase())
        );
        
        if (!matchesCategory) {
          option.style.display = 'none';
        } else {
          option.style.display = '';
        }
      }
    });
  }

  /**
   * Setup type filter text based on page type
   */
  setupTypeFilter() {
    const typeFilter = document.querySelector(
      '.strmpr-search-result-filters .strmpr-dropdown.strmpr-dropdown-product-type-event button span'
    );
    
    if (!typeFilter) return;

    const originalText = typeFilter.textContent.trim();
    const splitTexts = originalText.split('/');
    
    if (splitTexts.length < 2) return;

    const productTypeText = splitTexts[0].trim();
    const eventText = splitTexts[1].trim();

    if (this.pageType === 'designer-event') {
      typeFilter.textContent = eventText;
    } else if (this.pageType === 'find-retailer') {
      typeFilter.textContent = productTypeText;
    }
  }

  /**
   * Setup initial state for find-retailer page
   */
  setupInitialState() {
    if (this.pageType !== 'find-retailer') return;

    const { storemapperResults, mapContainer } = this.elements;

    // Hide results initially
    storemapperResults?.classList.add('hidden');

    // Hide existing map pins
    this.hideMapPins();

    // Setup observer for dynamically added pins
    if (mapContainer) {
      this.setupMapPinObserver(mapContainer);
    }
  }

  /**
   * Hide map pins
   */
  hideMapPins() {
    const mapPins = document.querySelectorAll('gmp-advanced-marker');
    mapPins.forEach(pin => pin.classList.add('hidden'));
  }

  /**
   * Setup observer for map pins
   */
  setupMapPinObserver(mapContainer) {
    const observer = new MutationObserver(() => {
      const newPins = document.querySelectorAll('gmp-advanced-marker:not(.observed)');
      newPins.forEach(pin => {
        pin.classList.add('hidden', 'observed');
      });
    });

    observer.observe(mapContainer, {
      childList: true,
      subtree: true
    });

    this.observers.set('mapPins', observer);
  }

  /**
   * Process URL parameters
   */
  processUrlParameters() {
    const collectionParam = this.getUrlParam('collection');
    if (!collectionParam) return;

    const options = document.querySelectorAll(
      '.strmpr-search-result-filters .strmpr-dropdown .strmpr-dropdown-option'
    );

    options.forEach(option => {
      const label = option.querySelector('label');
      const checkbox = option.querySelector('input[type="checkbox"]');

      if (label?.textContent.trim().toLowerCase() === collectionParam.trim().toLowerCase()) {
        if (!checkbox.checked) {
          checkbox.checked = true;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  /**
   * Arrange divs in proper structure
   */
  arrangeDivs() {
    this.movePageHeader();
    this.setupSearchButton();
  }

  /**
   * Move page header to search div
   */
  movePageHeader() {
    const { pageHeader } = this.elements;
    const searchDiv = this.storemapperDiv.querySelector('.strmpr-search');

    if (pageHeader && searchDiv) {
      pageHeader.classList.add('strmpr-header');
      searchDiv.insertBefore(pageHeader, searchDiv.firstChild);
    }
  }

  /**
   * Setup search button
   */
  setupSearchButton() {
    const { searchButton, filterBucket } = this.elements;

    if (searchButton && filterBucket) {
      searchButton.textContent = storemapperStrings.searchButton;
      filterBucket.appendChild(searchButton);
    }
  }

  /**
   * Add input labels
   */
  addInputLabels() {
    this.addShowMapToggle();
    this.addDistanceLabel();
    this.addSearchBoxLabel();
    this.addFiltersLabel();
  }

  /**
   * Add show map toggle
   */
  addShowMapToggle() {
    const { pageHeader } = this.elements;
    const searchDiv = this.storemapperDiv.querySelector('.strmpr-search');

    if (!searchDiv || !pageHeader) return;

    const showMapWrapper = this.createElement('div', 'show-map-wrapper', [
      this.createElement('span', 'show-map-label', [], 'Show map'),
      this.createElement('button', 'show-map-toggle', [
        this.createElement('span', 'show-map-toggle-circle')
      ])
    ]);

    const toggleButton = showMapWrapper.querySelector('.show-map-toggle');
    toggleButton.setAttribute('aria-pressed', 'true');
    
    toggleButton.addEventListener('click', () => this.toggleMap(toggleButton));

    pageHeader.insertAdjacentElement('afterend', showMapWrapper);
  }

  /**
   * Toggle map visibility
   */
  toggleMap(toggleButton) {
    const { mapContainer } = this.elements;
    if (!mapContainer) return;

    const isHidden = mapContainer.style.display === 'none';
    mapContainer.style.display = isHidden ? '' : 'none';
    toggleButton.setAttribute('aria-pressed', isHidden.toString());
  }

  /**
   * Add distance label
   */
  addDistanceLabel() {
    const distanceDiv = this.storemapperDiv.querySelector('.storemapper-distance .strmpr-distance-btn');
    if (distanceDiv) {
      const label = this.createElement('p', ['label-distance', 'storemapper-label'], [], storemapperStrings.within);
      distanceDiv.parentNode.insertBefore(label, distanceDiv);
    }
  }

  /**
   * Add search box label
   */
  addSearchBoxLabel() {
    const searchBoxDiv = this.storemapperDiv.querySelector('.strmpr-search-box-wrapper .strmpr-search-box');
    if (searchBoxDiv) {
      const label = this.createElement('p', ['label-search-box', 'storemapper-label'], [], storemapperStrings.zipCode);
      searchBoxDiv.parentNode.insertBefore(label, searchBoxDiv);
    }
  }

  /**
   * Add filters label
   */
  addFiltersLabel() {
    const { filterBucket } = this.elements;
    if (!filterBucket) return;

    const labelText = this.pageType === 'designer-event' ? storemapperStrings.searchDesign : storemapperStrings.searchRetailer;
    const label = this.createElement('p', ['label-search-result-filters', 'storemapper-label'], [], labelText);
    filterBucket.parentNode.insertBefore(label, filterBucket);
  }

  /**
   * Setup Search Results Observer
   */
  setupSearchResultsObserver() {
  const storemapperList = document.getElementById('storemapper-list');
  if (!storemapperList) return;

  if (this.observers.has('searchResults')) {
    this.observers.get('searchResults').disconnect();
  }

  const observer = new MutationObserver((mutations) => {
    let hasNewResults = false;
    
    mutations.forEach(mutation => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        const newResults = Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && 
          (node.classList?.contains('strmpr-search-result') || 
           node.querySelector?.('.strmpr-search-result'))
        );
        if (newResults) {
          hasNewResults = true;
        }
      }
    });

    if (hasNewResults) {
      setTimeout(() => {
        this.handleCustomFunctionalities();
      }, 100);
    }
  });

  observer.observe(storemapperList, {
    childList: true,
    subtree: true
  });

  this.observers.set('searchResults', observer);
}

  /**
   * Handle custom functionalities
   */
  handleCustomFunctionalities() {
    clearTimeout(this.functionalitiesTimeout);
    this.functionalitiesTimeout = setTimeout(() => {
      this.customFunctionalities();
    }, 50);
  }

  /**
   * Custom functionalities after search
   */
  customFunctionalities() {
    const searchResults = document.querySelectorAll('.strmpr-search-result[data-idx]');
    if (searchResults.length === 0) {
      setTimeout(() => {
        const retryResults = document.querySelectorAll('.strmpr-search-result[data-idx]');
        if (retryResults.length > 0) {
          this.performCustomFunctionalities();
        }
      }, 100);
      return;
    }

    this.performCustomFunctionalities();
  }

  /**
   * Perfrom Custom functionalities after search
   */
  performCustomFunctionalities() {
    this.addFilterResultsHeader();
    this.handleResultsVisibility();
    this.processSearchResults();
    this.setupImageModal();
    this.handleMapDesign();
  }

  /**
   * Add filter results header
   */
  addFilterResultsHeader() {
    const { filterBucket } = this.elements;
    if (!filterBucket || filterBucket.querySelector('.strmpr-header-result')) return;

    const headerText = this.pageType === 'find-retailer' ? storemapperStrings.retailerResult : storemapperStrings.eventResult;
    const header = this.createElement('h4', ['strmpr-header', 'strmpr-header-result'], [], headerText);
    filterBucket.appendChild(header);
  }

  /**
   * Handle results visibility for find-retailer page
   */
  handleResultsVisibility() {
    if (this.pageType !== 'find-retailer') return;

    const { storemapperResults, resetButton, searchZipButton } = this.elements;
    const mapPins = document.querySelectorAll('gmp-advanced-marker');

    if (resetButton) {
      resetButton.addEventListener('click', () => {
        storemapperResults?.classList.add('hidden');
        mapPins.forEach(pin => pin.classList.add('hidden'));
      });
    }

    // Show results if search is active
    if (searchZipButton?.value?.trim()) {
      storemapperResults?.classList.remove('hidden');
      mapPins.forEach(pin => pin.classList.remove('hidden'));
    }
  }

  /**
   * Process all search results efficiently in a single loop
   */
  processSearchResults() {
    const searchResults = document.querySelectorAll('.strmpr-search-result[data-idx]');
    if (searchResults.length === 0) return;

    // Process all search result related functionality in one loop
    searchResults.forEach((resultDiv, index) => {
      this.processIndividualResult(resultDiv, index);
    });

    // Handle elements that need to be processed separately
    this.processViewMoreButton();
    this.addResultDividers();
    this.processEventDates();
    this.processEventTypes();
  }

  /**
   * Process individual search result
   */
  processIndividualResult(resultDiv, index) {
    const idx = resultDiv.getAttribute('data-idx');

    // Setup map interaction
    this.setupMapInteraction(resultDiv, idx);

    // Add store number
    this.addStoreNumber(resultDiv, index);

    // Wrap store info elements
    this.wrapStoreInfo(resultDiv);

    // Handle featured retailers
    this.handleFeaturedRetailer(resultDiv);

    // Process booking buttons
    this.processBookingButtons(resultDiv);

    // Setup image modal for logo
    this.setupLogoImageModal(resultDiv);
  }

  /**
   * Setup map interaction for result
   */
  setupMapInteraction(resultDiv, idx) {
    const mapDiv = document.querySelector(`.storemapper_view[data-idx="${idx}"]`);
    if (!mapDiv) return;

    // Remove any existing event listeners
    const existingHandler = resultDiv._mapInteractionHandler;
    if (existingHandler) {
      resultDiv.removeEventListener('mouseenter', existingHandler);
      resultDiv.removeEventListener('click', existingHandler);
    }

    // Create a new handler
    const mapInteractionHandler = (event) => {
      if (event.target.tagName === 'BUTTON' || 
          event.target.tagName === 'A' || 
          event.target.closest('button') || 
          event.target.closest('a') ||
          event.target.hasAttribute('onclick')) {
        return;
      }

      if (event.type === 'mouseenter' && 'ontouchstart' in window) {
        return;
      }

      mapDiv.click();
    };

    // Store the handler reference for cleanup
    resultDiv._mapInteractionHandler = mapInteractionHandler;

    // Add event listeners
    resultDiv.addEventListener('mouseenter', mapInteractionHandler);
    resultDiv.addEventListener('click', mapInteractionHandler);
  }

  /**
   * Add store number to result
   */
  addStoreNumber(resultDiv, index) {
    const nameDiv = resultDiv.querySelector('.strmpr-field-name');
    if (!nameDiv || nameDiv.querySelector('.store-number')) return;

    const numberSpan = this.createElement('span', 'store-number', [], `${index + 1} `);
    nameDiv.prepend(numberSpan);
  }

  /**
   * Wrap store info elements
   */
  wrapStoreInfo(resultDiv) {
    const bucket = resultDiv.querySelector('.strmpr-field-bucket-general');
    if (!bucket) return;

    const directions = bucket.querySelector('.strmpr-field-directions.storemapper_directions');
    const url = bucket.querySelector('.strmpr-field-url');

    if (directions && url && !directions.parentElement.classList.contains('strmpr-store__info')) {
      const wrapper = this.createElement('div', 'strmpr-store__info');
      bucket.insertBefore(wrapper, directions);
      wrapper.appendChild(directions);
      wrapper.appendChild(url);
    }
  }

  /**
   * Handle featured retailer styling
   */
  handleFeaturedRetailer(resultDiv) {
    const featured = resultDiv.querySelector('.strmpr-field-custom.featured_retailer');
    if (!featured) return;

    featured.classList.remove('hidden');

    const featuredText = featured.textContent.trim();
    if (featuredText.match(/Featured:\s*Y/i) || featuredText === storemapperStrings.featured) {
      featured.textContent = storemapperStrings.featured;
    } else {
      featured.classList.add('hidden');
    }
  }

  /**
   * Process booking buttons
   */
  processBookingButtons(resultDiv) {
    this.processBookAppointmentButtons(resultDiv);
    this.processRequestAppointmentButtons(resultDiv);
  }

  /**
   * Process book appointment buttons
   */
  processBookAppointmentButtons(resultDiv) {
    const bookButtons = resultDiv.querySelectorAll('.strmpr-field-custom.direct_booking button');
    
    bookButtons.forEach(button => {
      if (button.dataset.parameterized) return;

      const retailerIdField = resultDiv.querySelector('.strmpr-field-custom.retailer_id');
      const bridalRetailIdField = resultDiv.querySelector('.strmpr-field-custom.bridal_retailid');
      const retailerNameField = resultDiv.querySelector('.strmpr-field-bucket-general .strmpr-field-name');

      const retailerId = this.extractFieldValue(retailerIdField);
      const bridalRetailId = this.extractFieldValue(bridalRetailIdField);
      const retailerName = retailerNameField ? this.extractRetailerName(retailerNameField) : '';

      if (retailerId || bridalRetailId) {
        this.updateButtonOnclick(button, { 
          retailerId, 
          bridalretailid: bridalRetailId ,
          retailerName: retailerName
        });
        button.dataset.parameterized = 'true';
      }
    });
  }

  /**
   * Process request appointment buttons
   */
  processRequestAppointmentButtons(resultDiv) {
    const requestButtons = resultDiv.querySelectorAll('.strmpr-field-custom.req_booking button');
    
    requestButtons.forEach(button => {
      if (button.dataset.parameterized) return;

      const retailerIdField = resultDiv.querySelector('.strmpr-field-custom.retailer_id');
      const emailField = resultDiv.querySelector('.strmpr-field-bucket-general .strmpr-field-email a');
      const retailerNameField = resultDiv.querySelector('.strmpr-field-bucket-general .strmpr-field-name');
      
      const retailerId = this.extractFieldValue(retailerIdField);
      const email = emailField ? emailField.getAttribute('href').replace('mailto:', '') : '';
      const retailerName = retailerNameField ? this.extractRetailerName(retailerNameField) : '';

      if (retailerId) {
        this.updateButtonOnclick(button, { 
          id: retailerId,
          email: email,
          retailerName: retailerName
        });
        button.dataset.parameterized = 'true';
      }
    });
  }

  /**
   * Extract retailer name excluding store number
   */
  extractRetailerName(nameField) {
    // Clone the element to avoid modifying the original
    const tempElement = nameField.cloneNode(true);
    
    // Remove the store number span
    const storeNumberSpan = tempElement.querySelector('.store-number');
    if (storeNumberSpan) {
      storeNumberSpan.remove();
    }
    
    let retailerName = tempElement.textContent.trim();
    retailerName = retailerName.replace(/\s+/g, ' ').trim();
    
    return retailerName;
  }

  /**
   * Custom URL encoding that handles apostrophes properly
   */
  customEncodeURIComponent(str) {
    let encoded = encodeURIComponent(str.toString().trim());
    encoded = encoded.replace(/'/g, '%27');
    return encoded;
  }

  /**
   * Update button onclick with parameters
   */
  updateButtonOnclick(button, params) {
    const originalOnclick = button.getAttribute('onclick');
    const urlMatch = originalOnclick?.match(/window\.open\(['"]([^'"]+)['"]/);
    
    if (!urlMatch) return;

    const baseUrl = urlMatch[1];
    
    // Build parameter strings with proper encoding
    const paramStrings = Object.entries(params)
      .filter(([, value]) => value && value.toString().trim() !== '')
      .map(([key, value]) => {
        const encodedValue = this.customEncodeURIComponent(value);
        return `${key}=${encodedValue}`;
      });

    if (paramStrings.length === 0) return;

    const separator = baseUrl.includes('?') ? '&' : '?';
    const newUrl = baseUrl + separator + paramStrings.join('&');
    
    const newOnclick = originalOnclick.replace(baseUrl, newUrl);
    
    button.setAttribute('onclick', newOnclick);
  }

  /**
   * Extract field value from custom field
   */
  extractFieldValue(field) {
    return field ? field.textContent.trim().split(': ')[1] || '' : '';
  }

  /**
   * Setup logo image modal
   */
  setupLogoImageModal(resultDiv) {
    const logoImg = resultDiv.querySelector('.strmpr-field-logo img');
    if (!logoImg || this.processedElements.has(logoImg)) return;

    logoImg.style.cursor = 'pointer';
    logoImg.addEventListener('click', (event) => {
      event.stopPropagation();
      this.showImageModal(logoImg.src);
    });

    this.processedElements.add(logoImg);
  }

  /**
   * Process view more button
   */
  processViewMoreButton() {
    const viewMoreButton = document.querySelector('.strmpr-view-more-stores-button');
    viewMoreButton?.classList.add('button');
  }

  /**
   * Add dividers between results
   */
  addResultDividers() {
    // Remove existing dividers
    document.querySelectorAll('.strmpr-divider').forEach(divider => divider.remove());

    const items = document.querySelectorAll('#storemapper-list .strmpr-search-result');
    items.forEach((item, index) => {
      if (index !== items.length - 1) {
        const divider = this.createElement('div', 'strmpr-divider', [], ' ');
        item.parentNode.insertBefore(divider, item.nextSibling);
      }
    });
  }

  /**
   * Process event dates
   */
  processEventDates() {
    const eventStartDate = document.querySelectorAll('.strmpr-search-result .strmpr-field-custom.event_start-date');
    const eventEndDate = document.querySelectorAll('.strmpr-search-result .strmpr-field-custom.event_end-date');

    eventStartDate.forEach((startElement, index) => {
      const endElement = eventEndDate[index];
      if (!endElement) return;

      const formattedDate = this.formatEventDateRange(startElement.textContent, endElement.textContent);
      if (formattedDate) {
        startElement.textContent = formattedDate;
      }
    });
  }

  /**
   * Format event date range
   */
  formatEventDateRange(startText, endText) {
    const startColonIndex = startText.indexOf(':');
    const endColonIndex = endText.indexOf(':');

    if (startColonIndex === -1 || endColonIndex === -1) return null;

    const startDate = startText.substring(startColonIndex + 1).trim();
    const endDate = endText.substring(endColonIndex + 1).trim();

    // Check if dates are in MM/DD/YYYY format
    if (this.isSlashDateFormat(startDate) && this.isSlashDateFormat(endDate)) {
      return this.formatSlashDateRange(startDate, endDate);
    }

    // Handle existing "Jun 02, 2025" format
    const startParts = startDate.split(' ');
    const endParts = endDate.split(' ');

    if (startParts.length >= 3 && endParts.length >= 3) {
      const startMonth = startParts[0];
      const startDay = startParts[1].replace(',', '');
      const endDay = endParts[1].replace(',', '');
      const endYear = endParts[2];

      return `${startMonth} ${startDay}-${endDay}, ${endYear}`;
    }

    return null;
  }

  /**
   * Check if date is in MM/DD/YYYY format
   */
  isSlashDateFormat(dateString) {
    return /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString);
  }

  /**
   * Format slash date range (MM/DD/YYYY to "Mon DD-DD, YYYY")
   */
  formatSlashDateRange(startDate, endDate) {
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    try {
      const startParts = startDate.split('/');
      const endParts = endDate.split('/');

      if (startParts.length !== 3 || endParts.length !== 3) return null;

      const startMonth = parseInt(startParts[0]) - 1; // Convert to 0-based index
      const startDay = parseInt(startParts[1]);
      const startYear = parseInt(startParts[2]);

      const endMonth = parseInt(endParts[0]) - 1;
      const endDay = parseInt(endParts[1]);
      const endYear = parseInt(endParts[2]);

      // Validate month indices
      if (startMonth < 0 || startMonth > 11 || endMonth < 0 || endMonth > 11) {
        return null;
      }

      // If same month and year, format as "Jan 17-24, 2025"
      if (startMonth === endMonth && startYear === endYear) {
        return `${monthNames[startMonth]} ${startDay}-${endDay}, ${startYear}`;
      }

      // If different months or years, format as "Jan 17 - Feb 24, 2025"
      if (startYear === endYear) {
        return `${monthNames[startMonth]} ${startDay} - ${monthNames[endMonth]} ${endDay}, ${startYear}`;
      } else {
        return `${monthNames[startMonth]} ${startDay}, ${startYear} - ${monthNames[endMonth]} ${endDay}, ${endYear}`;
      }
    } catch (error) {
      console.warn('Error formatting slash date range:', error);
      return null;
    }
  }

  /**
   * Process event types
   */
  processEventTypes() {
    const eventTypes = document.querySelectorAll('.strmpr-search-result .strmpr-field-custom.strmpr_event-type');
    
    eventTypes.forEach(element => {
      const text = element.textContent.trim();
      const colonIndex = text.indexOf(':');
      
      if (colonIndex !== -1) {
        const valueAfterColon = text.substring(colonIndex + 1).trim();
        element.textContent = valueAfterColon;
      }
    });
  }

  /**
   * Setup image modal
   */
  setupImageModal() {
    let imageModal = document.getElementById('strmpr-image-modal');
    if (imageModal) return;

    imageModal = this.createElement('div', 'strmpr-image-modal', [], `
      <div class="strmpr-modal-content">
        <div class="strmpr-modal-inner">
          <span class="strmpr-modal-close">
            <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="14.9194" y="4.99121" width="0.833333" height="13.3333" transform="rotate(45 14.9194 4.99121)" fill="#58514C"/>
              <path d="M15.5087 14.4194L14.9194 15.0087L5.49132 5.5806L6.08058 4.99134L15.5087 14.4194Z" fill="#58514C"/>
            </svg>
          </span>
          <img class="strmpr-modal-img" src="" alt="Store Image">
        </div>
      </div>
    `);

    imageModal.id = 'strmpr-image-modal';
    document.body.appendChild(imageModal);

    // Setup modal event listeners
    const closeBtn = imageModal.querySelector('.strmpr-modal-close');
    closeBtn.addEventListener('click', () => this.hideImageModal());
    
    imageModal.addEventListener('click', (event) => {
      if (event.target === imageModal) {
        this.hideImageModal();
      }
    });
  }

  /**
   * Show image modal
   */
  showImageModal(imageSrc) {
    const imageModal = document.getElementById('strmpr-image-modal');
    const modalImg = imageModal?.querySelector('.strmpr-modal-img');
    
    if (modalImg && imageModal) {
      modalImg.src = imageSrc;
      imageModal.style.display = 'block';
    }
  }

  /**
   * Hide image modal
   */
  hideImageModal() {
    const imageModal = document.getElementById('strmpr-image-modal');
    if (imageModal) {
      imageModal.style.display = 'none';
    }
  }

  /**
   * Handle map design - adjust grid row end based on search state
   */
  handleMapDesign() {
    const { mapContainer } = this.elements;
    const errorElement = document.getElementById('storemapper-error');
    const storemapperList = document.getElementById('storemapper-list');

    if (!mapContainer) return;

    // Primary check: Set grid row end based on storemapper-list children count
    if (storemapperList) {
      if (storemapperList.children.length <= 1) {
        mapContainer.style.gridRowEnd = '5';
      } else {
        mapContainer.style.gridRowEnd = '4';
      }
    }

    // Set grid row end if there's an error message
    if (errorElement && errorElement.innerHTML.trim() !== '') {
      mapContainer.style.gridRowEnd = '5';
    }
  }

  /**
   * Handle map design on search button click
   */
  handleMapDesignOnSearch() {
    const { mapContainer } = this.elements;
    if (!mapContainer) return;

    // Recheck storemapper-list after search with a slight delay
    setTimeout(() => {
      const storemapperList = document.getElementById('storemapper-list');
      if (storemapperList) {
        if (storemapperList.children.length <= 1) {
          mapContainer.style.gridRowEnd = '5';
        } else {
          mapContainer.style.gridRowEnd = '4';
        }
      }
    }, 100);
  }

  /**
   * Handle map design on reset button click
   */
  handleMapDesignOnReset() {
    const { mapContainer } = this.elements;
    if (!mapContainer) return;

    // After reset, list should be empty, so set to 5
    mapContainer.style.gridRowEnd = '5';
  }

  /**
   * Create element helper
   */
  createElement(tag, className = '', children = [], textContent = '') {
    const element = document.createElement(tag);
    
    if (className) {
      if (Array.isArray(className)) {
        element.classList.add(...className);
      } else {
        element.className = className;
      }
    }
    
    if (textContent) {
      element.innerHTML = textContent;
    }
    
    children.forEach(child => {
      if (child) element.appendChild(child);
    });
    
    return element;
  }

  /**
   * Cleanup observers
   */
  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    clearTimeout(this.functionalitiesTimeout);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new StoreMapper();
});